import { http } from '@frontend/fetch';
import { useTranslation } from '@frontend/i18n';
import { Button, Text, TextField, useForm, useAlert } from '@frontend/design-system';
import { logError } from '../../utils/loggers';
import { BlockedNumberCreatePayload } from './types';
import { useCallBlockMutations } from './use-call-block-mutations';

export const BlockNumberForm = ({
  phoneTenantId,
  blockedNumbers,
}: {
  phoneTenantId?: string;
  blockedNumbers?: string[];
}) => {
  const { t } = useTranslation('phone', { keyPrefix: 'call-blocking' });
  const alert = useAlert();
  const { formProps, getFieldProps, isComplete, reset } = useForm({
    fields: {
      phone: {
        required: true,
        type: 'phone',
      },
    },
    onSubmit: () => {
      createFn({
        tenantId: phoneTenantId || '',
        phoneNumber: {
          countryCode: 1,
          nationalNumber: Number(getFieldProps('phone').value) || undefined,
        },
      });
    },
  });

  const { create } = useCallBlockMutations(phoneTenantId || '');

  const createFn = (payload: BlockedNumberCreatePayload) =>
    create.mutate(payload, {
      onSuccess: () => {
        alert.success(t('Phone number blocked successfully.'));
        reset();
      },
      onError: (error) => {
        http.isHttpError(error) && error.message?.includes('blocked number not unique for create operation')
          ? alert.error(t('This number has already been blocked.'))
          : alert.error(t('Failed to add number.'));
        logError('BlockNumberForm:createBlockedNumberMutation | Failed to block number', {
          data: { phoneTenantId, error },
        });
      },
    });

  const phoneFieldProps = getFieldProps('phone');
  // checked directly against the latest prop (not through useForm's validator) so it isn't gated by "touched"
  const isAlreadyBlocked = !!phoneFieldProps.value && !!blockedNumbers?.includes(phoneFieldProps.value);
  // Only show the error on change if the field has been touched or the number is already blocked
  const showError = isAlreadyBlocked || (phoneFieldProps.touched && !!phoneFieldProps.error);
  const errorMessage = isAlreadyBlocked ? t('This number has already been blocked.') : phoneFieldProps.error;

  return (
    <form {...formProps}>
      <div
        css={{
          display: 'flex',
          gap: 'var(--fabric-space-gap-md)',
        }}
      >
        <span
          css={{
            display: 'inline-block',
            width: '300px',
          }}
        >
          <TextField
            {...phoneFieldProps}
            error={errorMessage}
            touched={showError}
            aria-invalid={showError}
            label={t('Phone Number')}
          />
          {!showError && (
            <Text css={{ marginTop: 'var(--fabric-space-gap-xxs)' }} size='small' color='light'>
              {t('Enter the phone number you want to block')}
            </Text>
          )}
        </span>
        <Button destructive type='submit' disabled={!isComplete || isAlreadyBlocked} size='large'>
          {t('Block Number')}
        </Button>
      </div>
    </form>
  );
};
